import { useLocation } from 'preact-iso';
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import useSWR from 'swr';
import waitForTimers from 'wait-for-timers';
import appStoreBadgeUrl from '../../assets/icons-standalone/app-store-badge-black.svg';
import googlePlayBadgeUrl from '../../assets/icons-standalone/google-play-badge.png';
import Footer from '../../components/footer';
import { Link } from '../../components/link';
import TopNav from '../../components/top-nav';
import { fetchDonations } from '../../utils/api';
import { usePageTitle } from '../../utils/page-title';
import { platform } from '../../utils/platform';
import addScrollListener from '../../utils/scroll-listener';
import style from './style.module.scss';

export default function Home() {
  usePageTitle('Adblock Podcast | Skip ads, support creators', false);
  const [isAtTop, setAtTop] = useState(true);
  const [isAnimated, setAnimated] = useState(false);
  const { route } = useLocation();

  useLayoutEffect(() => {
    return waitForTimers(['raf', 'raf'], () => setAnimated(true));
  }, []);

  useEffect(() => {
    return addScrollListener(window, () => {
      setAtTop(window.scrollY === 0);
    });
  }, []);

  const { data: donations } = useSWR('donations', fetchDonations);

  return (
    <>
      <TopNav isTransparent={isAtTop} />
      <div class={style.topSection}>
        <h1
          style={{ marginTop: 'auto' }}
          className={`${style.h1} will-fade-in ${isAnimated ? 'fade-in' : ''}`}
        >
          Finally, a podcast app <br />
          that&nbsp;<strong>skips&nbsp;ads</strong>!
        </h1>
        <em
          style={{
            '--fade-in-delay': '0.4s',
          }}
          className={`${style.tagline} will-fade-in ${isAnimated ? 'fade-in' : ''}`}
        >
          Adblock Podcast detects &amp; skips ads on most&nbsp;podcasts.
          <br />
          Try the beta release in your app store!
        </em>
        <div
          class={`will-fade-in flex flex-wrap items-center justify-center gap-2 ${isAnimated ? 'fade-in' : ''}`}
          style={{
            '--fade-in-delay': '0.6s',
          }}
        >
          {platform === 'Web' && (
            <>
              <a href="https://apps.apple.com/us/app/adblock-podcast/id1662226203">
                <img
                  class="h-10 w-auto"
                  src={appStoreBadgeUrl}
                  width="120"
                  height="40"
                  alt="Download on the App Store (iOS)"
                />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.adblockpodcast.android.uamp.next">
                <img
                  class="h-10 w-auto"
                  src={googlePlayBadgeUrl}
                  alt="Get it on Google Play"
                  width="270"
                  height="80"
                />
              </a>
            </>
          )}
          <button
            style={{
              fontSize: '16px',
              '--button-height': '38px',
            }}
            class="button button--transparent"
            onClick={() => route('/now')}
          >
            {platform === 'Web' ? 'Open in Browser' : 'Open App'}
          </button>
        </div>
        <svg
          style={{
            height: 'calc(3vh + 2vw)',
            marginTop: 'auto',
            marginBottom: '-1px',
            transform: 'scaleY(-1)',
          }}
          width="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path d="M0 100 C 20 0 50 0 100 100 L 100 -1 0 -1 Z" fill="white"></path>
        </svg>
      </div>

      <section id="about" class={style.aboutSection}>
        <h2 className="text-xl">How it works</h2>
        <ul class="mt-2 list-disc pl-4">
          <li>Listen to almost any podcast ad-free</li>
          <li>Affordable subscription supports podcasters</li>
        </ul>
        <p>
          See the <Link href="/pricing">Pricing / FAQ page</Link> to learn how your
          monthly subscription supports podcast creators. We set aside 4 cents per
          ad-skip (40 CPM) to help compensate for lost impressions.{' '}
          {donations ? (
            <>
              To date we've{' '}
              <Link href="/pricing#creator-compensation">
                donated over $
                {(Math.floor(donations.totalPaidCents / 1000) * 10).toLocaleString()}!
              </Link>
            </>
          ) : (
            <>
              <em>Loading stats...</em>
            </>
          )}
        </p>
      </section>
      <section class={style.aboutSection}>
        <h2 className="text-xl">About this project</h2>
        <p>
          Adblock Podcast has been my side project for over 4 years, and I’m really
          excited to finally get it out into the world!
        </p>
        <p>There are three goals for this project:</p>
        <ol class="list-decimal pl-4">
          <li>
            <strong>Skip ads</strong>, especially obtrusive ones that disrupt the flow
            of the podcast.
          </li>
          <li>
            <strong>Reduce consumption.</strong> Ads often prompt us to buy stuff we
            don’t need, increasing our carbon footprint and making us less generous,
            secure, and content.
          </li>
          <li>
            <strong>Compensate podcast creators!</strong> I love podcasts and admire the
            people who spend so much time crafting them. Ads currently play an essential
            role in this ecosystem. This project compensates creators for lost revenue
            due to ad skipping and avoids skewing download counts to ensure ad companies
            aren't paying for ads that never get listened to.
          </li>
        </ol>
        <br />
        <p>
          Feel free to try out the beta version of the app, and don’t hesitate to{' '}
          <a href="mailto:adblockpodcast@gmail.com" target="_blank">
            reach out to me (Micah)
          </a>{' '}
          if you have any questions or suggestions! I just launched in July 2024 so
          there's still a lot of room for improvement.
        </p>
      </section>
      <Footer />
    </>
  );
}
